import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PlusIcon, TrashIcon, CheckIcon, PencilSimpleIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Admin catalog of service types (Oil Change, Brake Service, ...).
 * Each type can carry a `default_interval_mi` that auto-fills the "next due"
 * mileage when a new service is scheduled for that type.
 */
export default function ServiceTypesSettings() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newInterval, setNewInterval] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editVals, setEditVals] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/service-types");
      setTypes(data || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addType = async () => {
    if (!newLabel.trim()) return toast.error("Label is required");
    try {
      // Derive key from label — user only types the human name.
      const key = newLabel.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
      await api.post("/service-types", {
        key,
        label: newLabel.trim(),
        default_interval_mi: newInterval ? Number(newInterval) : null,
        active: true,
      });
      toast.success("Service type added");
      setNewLabel(""); setNewInterval(""); setAdding(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEditVals({ label: t.label, default_interval_mi: t.default_interval_mi ?? "", active: t.active !== false });
  };
  const saveEdit = async (t) => {
    try {
      await api.put(`/service-types/${t.id}`, {
        key: t.key,
        label: editVals.label,
        default_interval_mi: editVals.default_interval_mi === "" ? null : Number(editVals.default_interval_mi),
        active: editVals.active,
      });
      toast.success("Saved");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.label}"? Services already using this type will block deletion — deactivate instead if needed.`)) return;
    try {
      await api.delete(`/service-types/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="service-types-settings">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500 font-bold">/// Catalog</div>
          <div className="text-sm text-slate-600">Add, rename, or deactivate the service types available on the Services page.</div>
        </div>
        <Button onClick={() => setAdding(true)} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-service-type">
          <PlusIcon size={14} weight="bold" className="mr-2" /> Add type
        </Button>
      </div>

      {adding && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-wider font-bold">Label</Label>
            <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. Transmission Service" className="rounded-sm mt-1" data-testid="new-type-label" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Default interval (mi)</Label>
            <Input type="number" value={newInterval} onChange={(e) => setNewInterval(e.target.value)} placeholder="e.g. 25000" className="rounded-sm mt-1" data-testid="new-type-interval" />
          </div>
          <div className="flex gap-2">
            <Button onClick={addType} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="save-service-type">Save</Button>
            <Button variant="outline" onClick={() => { setAdding(false); setNewLabel(""); setNewInterval(""); }} className="rounded-sm">Cancel</Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-300 rounded-sm">
        <table className="fleet-table">
          <thead>
            <tr>
              <th>Label</th>
              <th className="font-mono">Key</th>
              <th>Default interval</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center py-6 text-sm text-slate-500 font-mono">Loading...</td></tr>}
            {!loading && types.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-sm text-slate-500">No service types yet — add one.</td></tr>
            )}
            {types.map((t) => (
              <tr key={t.id} data-testid={`service-type-row-${t.id}`}>
                <td>
                  {editingId === t.id ? (
                    <Input value={editVals.label} onChange={(e) => setEditVals({ ...editVals, label: e.target.value })} className="rounded-sm" />
                  ) : (
                    <span className="font-semibold">{t.label}</span>
                  )}
                </td>
                <td className="font-mono text-xs text-slate-500">{t.key}</td>
                <td>
                  {editingId === t.id ? (
                    <Input type="number" value={editVals.default_interval_mi} onChange={(e) => setEditVals({ ...editVals, default_interval_mi: e.target.value })} className="rounded-sm w-32" placeholder="—" />
                  ) : (
                    <span className="font-mono">{t.default_interval_mi ? `${Number(t.default_interval_mi).toLocaleString()} mi` : "—"}</span>
                  )}
                </td>
                <td>
                  {editingId === t.id ? (
                    <Switch checked={editVals.active} onCheckedChange={(v) => setEditVals({ ...editVals, active: v })} />
                  ) : (
                    <span className={`text-xs font-bold ${t.active === false ? "text-slate-400" : "text-emerald-700"}`}>
                      {t.active === false ? "INACTIVE" : "ACTIVE"}
                    </span>
                  )}
                </td>
                <td className="text-right space-x-2">
                  {editingId === t.id ? (
                    <>
                      <Button size="sm" onClick={() => saveEdit(t)} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid={`save-type-${t.id}`}>
                        <CheckIcon size={13} weight="bold" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="rounded-sm">Cancel</Button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(t)} className="text-blue-600 hover:underline text-xs font-semibold" data-testid={`edit-type-${t.id}`}>
                        <PencilSimpleIcon size={12} weight="bold" className="inline mr-1" /> Edit
                      </button>
                      <button onClick={() => remove(t)} className="text-red-600 hover:underline text-xs font-semibold" data-testid={`delete-type-${t.id}`}>
                        <TrashIcon size={12} weight="bold" className="inline mr-1" /> Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
